import { isAxiosError } from "axios"
import { DUPLICATE_COMPANY_MESSAGE_SUBSTR } from "../constants"
import type { DuplicateCompanyRow } from '../types'

/** Thrown when user dismisses the duplicate-address confirm without forcing save. */
export class CompanyDuplicateAddressCancelledError extends Error {
  constructor() {
    super("Company duplicate address dialog cancelled")
    this.name = "CompanyDuplicateAddressCancelledError"
  }
}

export function isCompanyDuplicateAddressCancelledError(
  e: unknown,
): e is CompanyDuplicateAddressCancelledError {
  return e instanceof CompanyDuplicateAddressCancelledError
}

/**
 * Bodies to inspect, in order:
 * 1. Raw HTTP JSON (`AxiosError.response.data` or apiClient `error.data`) — same as Angular `error.error`.
 * 2. Full rejected value from the response interceptor (`{ message, status, data: <HTTP JSON> }`).
 */
function collectDuplicateCandidateBodies(error: unknown): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = []

  if (isAxiosError(error)) {
    const d = error.response?.data
    if (d && typeof d === "object" && !Array.isArray(d)) {
      out.push(d as Record<string, unknown>)
    }
    return out
  }

  if (error && typeof error === "object" && !Array.isArray(error)) {
    const e = error as Record<string, unknown> & { data?: unknown }
    const inner = e.data
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
      out.push(inner as Record<string, unknown>)
    }
    out.push(e)
  }

  return out
}

function envelopeMessage(error: unknown): string {
  if (error && typeof error === "object" && !Array.isArray(error)) {
    const m = (error as { message?: unknown }).message
    if (typeof m === "string" && m.trim()) return m
  }
  return ""
}

/**
 * apiClient response interceptor rejects with `{ message, data: <full API JSON> }`.
 * Duplicate rows may be at `data` (array), `data.data` (nested), or `message` on the inner JSON only.
 */
function duplicateMessageFromBody(body: Record<string, unknown>): string {
  const wrapped = body.data
  if (wrapped && typeof wrapped === "object" && !Array.isArray(wrapped)) {
    const inner = (wrapped as Record<string, unknown>).message
    if (typeof inner === "string" && inner.trim()) return inner.trim()
  }
  const top = typeof body.message === "string" ? body.message.trim() : ""
  if (top) return top
  const errStr = typeof body.error === "string" ? body.error.trim() : ""
  return errStr
}

function duplicateRowsFromBody(body: Record<string, unknown>): unknown[] | null {
  const d = body.data
  if (Array.isArray(d) && d.length > 0) return d
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const nested = (d as Record<string, unknown>).data
    if (Array.isArray(nested) && nested.length > 0) return nested
  }
  return null
}

function rowsToDuplicateCompanies(raw: unknown[]): DuplicateCompanyRow[] | null {
  const out: DuplicateCompanyRow[] = []
  for (const row of raw) {
    if (!row || typeof row !== "object") continue
    const r = row as Record<string, unknown>
    const id = r.id ?? r.company_id
    const name = r.name ?? r.company_name
    if (id != null && id !== "" && name != null && String(name).trim() !== "") {
      out.push({ id: id as number | string, name: String(name) })
    }
  }
  return out.length ? out : null
}

/**
 * Parses duplicate-address response from `POST /api/company` or `POST /api/company/:id`
 * (Angular `error.error`, or the same payload under apiClient's `error.data` from `interceptors.ts`).
 */
export function parseDuplicateCompanyAddressFromApiError(error: unknown): DuplicateCompanyRow[] | null {
  const candidates = collectDuplicateCandidateBodies(error)
  if (!candidates.length) return null

  const fallbackMsg = envelopeMessage(error)

  for (const body of candidates) {
    let msg = duplicateMessageFromBody(body)
    if (!msg.trim() && fallbackMsg) msg = fallbackMsg
    if (!msg.toLowerCase().includes(DUPLICATE_COMPANY_MESSAGE_SUBSTR)) continue

    const raw = duplicateRowsFromBody(body)
    if (!raw) continue

    const parsed = rowsToDuplicateCompanies(raw)
    if (parsed) return parsed
  }

  return null
}

